import { useCallback, useEffect, useReducer } from "react";

const COPIED_STATUS_DURATION_MS = 6000;

interface MessageCopyState {
  isCopied: boolean;
  copyToken: number;
}

type MessageCopyAction = { type: "copy" } | { type: "reset"; copyToken: number };

const messageCopyInitialState: MessageCopyState = {
  isCopied: false,
  copyToken: 0,
};

const messageCopyReducer = (
  state: MessageCopyState,
  action: MessageCopyAction,
): MessageCopyState => {
  switch (action.type) {
    case "copy":
      return { isCopied: true, copyToken: state.copyToken + 1 };
    case "reset":
      return action.copyToken === state.copyToken
        ? { ...state, isCopied: false }
        : state;
    default:
      return state;
  }
};

export const useMessageCopyState = (textToCopy: string) => {
  const [state, dispatch] = useReducer(messageCopyReducer, messageCopyInitialState);

  const copyResponseText = useCallback(() => {
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => dispatch({ type: "copy" }))
      .catch(() => {});
  }, [textToCopy]);

  useEffect(() => {
    if (!state.isCopied) return;
    const timeoutId = setTimeout(
      () => dispatch({ type: "reset", copyToken: state.copyToken }),
      COPIED_STATUS_DURATION_MS,
    );
    return () => clearTimeout(timeoutId);
  }, [state.isCopied, state.copyToken]);

  return { isCopied: state.isCopied, copyResponseText };
};
